import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import Button from "react-md/lib/Buttons/Button";
import { MSpinner } from "components/Shared";
import { getSketch } from "actions/homeowner/caseforms";
import SketchImageWrapper from "components/Homeowner/CaseForms/ReadOnly/SketchImageWrapper";
import PublicPropertyRecord from "components/Homeowner/CaseForms/PublicPropertyRecord";
import { WrappedMSplitTabView } from "components/Shared/MSplitTabView";
import { DataVerifiedCaseFormResult, Sketch as WebApiSketch } from "interfaces/Mueller.Business.Models";
import { userHasRight } from "selectors/rights";
import { AI_FORM_VERIFICATION_RESULTS_VIEW } from "typeConstants/rights";
import HiddenFromQAMessage from "components/PhoenixFormsV4/HiddenFromQAMessage";

interface Props {
    // passed in
    controlNumber: number;
    readOnly: boolean;
    isSketchPopOutOpen: boolean;
    onOpenSketchPopout: (sketchUri: string) => void;
    aiVerificationResults: DataVerifiedCaseFormResult,

    // From redux
    sketches: WebApiSketch[],
    fetchingSketches: boolean,
    getSketch: (controlNumber: number) => void,
    canViewAIFormResults: boolean;
};

const Sketch = ({
    controlNumber,
    readOnly,
    sketches,
    fetchingSketches,
    getSketch,
    canViewAIFormResults,
    aiVerificationResults,
    isSketchPopOutOpen,
    onOpenSketchPopout,
} : Props) => {
    
    const wasSketchPopOutOpen = useRef<boolean>(isSketchPopOutOpen);

    useEffect(() => {
        getSketch(controlNumber);
    }, []);

    useEffect(() => {
        if (wasSketchPopOutOpen.current && !isSketchPopOutOpen) {
            getSketch(controlNumber);
        }
        wasSketchPopOutOpen.current = isSketchPopOutOpen;
    }, [isSketchPopOutOpen]);

    const getSketchChild = (sketch) => {
        const sketchImages = sketch.sketchImages.map((item, ix) => {
            return <SketchImageWrapper key={ix} src={item} number={ix + 1} />;
        });            

        if (readOnly) {
            return (
                <div className="case-header">
                    {sketchImages}
                </div>
            );
        }
        const popout = () => onOpenSketchPopout(sketch.uri);
        return (
            <div className="case-header">
                {!!(aiVerificationResults?.formCanBeHiddenFromQa && canViewAIFormResults) && <><HiddenFromQAMessage /><br /></>}
                <PublicPropertyRecord controlNumber={controlNumber} />
                {
                    !isSketchPopOutOpen &&
                    <Button
                        flat
                        primary
                        swapTheming
                        onClick={() => {
                            if (!isSketchPopOutOpen) {
                                popout();
                            }
                        }}
                        style={{ marginBottom: "15px", marginTop: "15px" }}>
                        Open Sketch
                    </Button>
                }
                <br />
                <div>
                    {sketchImages}
                </div>
            </div>
        );
    };

    if (fetchingSketches) {
        return <MSpinner />;
    }

    // the pop up window calls this function on the parent window, so it needs to be defined.
    // classic implementation uses this to reload the images, but we are doing that automatically when the window is closed.
    window["message"] = () => true; 

    const sections = sketches.map(s => ({
        id: s.sketchName,
        title: s.sketchName,
        children: getSketchChild(s),
        cmsTopicKey: s.cmsTopicKey,
    }));

    return (
        <WrappedMSplitTabView
            tabs={sections}
        />
    );
};

const mapStateToProps = state => ({
    fetchingSketches: state.caseForms.fetchingSketches,
    sketches: state.caseForms.sketches,
    canViewAIFormResults: userHasRight(AI_FORM_VERIFICATION_RESULTS_VIEW)(state),
});

const mapDispatchToProps = dispatch => ({
    getSketch(controlIdNumber) {
        dispatch(getSketch(controlIdNumber));
    }
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(Sketch);

